import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  
  console.log('Current User:', currentUser);

  // Calculate time remaining until next password change is allowed
  const getTimeUntilNextChange = () => {
    const lastChangeDateTime = currentUser?.lastPasswordChange;
    if (!lastChangeDateTime) return null;

    const lastChange = new Date(lastChangeDateTime);
    const now = new Date();
    const timeDiff = now.getTime() - lastChange.getTime();
    const hoursElapsed = timeDiff / (1000 * 60 * 60);
    
    if (hoursElapsed >= 24) return null;
    
    const hoursRemaining = 24 - hoursElapsed;
    const hours = Math.floor(hoursRemaining);
    const minutes = Math.floor((hoursRemaining - hours) * 60);
    
    return { hours, minutes, totalHours: hoursRemaining };
  };

  const timeUntilNextChange = getTimeUntilNextChange();

  // Password strength checker - updated for alphanumeric only
  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (password.length >= 12) strength += 1; // Bonus for longer passwords
    if (/[A-Z]/.test(password)) strength += 1; // Has uppercase
    if (/[0-9]/.test(password)) strength += 1; // Has numbers
    if (/[a-z]/.test(password)) strength += 1; // Has lowercase
    
    // Cap the strength at 4 for consistency with UI
    setPasswordStrength(Math.min(strength, 4));
  };

  // Validate alphanumeric characters only
  const isValidPassword = (password) => {
    return /^[a-zA-Z0-9]+$/.test(password);
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    checkPasswordStrength(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    debugger 
    // Check 24-hour restriction first
    const lastChangeDateTime = currentUser?.lastPasswordChange;
    if (lastChangeDateTime) {
      const lastChange = new Date(lastChangeDateTime);
      const now = new Date();
      const timeDifference = now.getTime() - lastChange.getTime();
      const hoursElapsed = timeDifference / (1000 * 60 * 60); // Convert to hours
      
      if (hoursElapsed < 24) {
        const hoursRemaining = 24 - hoursElapsed;
        const hours = Math.floor(hoursRemaining);
        const minutes = Math.floor((hoursRemaining - hours) * 60);
        setError(`You can only change your password once every 24 hours. Please wait ${hours} hours and ${minutes} minutes before trying again.`);
        return;
      }
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from your current password');
      return;
    }
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }
    
    // Updated password validation
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }
    
    if (newPassword.length > 16) {
      setError('New password must not exceed 16 characters');
      return;
    }
    
    if (!isValidPassword(newPassword)) {
      setError('New password must contain only letters and numbers (no special characters)');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      
      const changeDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;

      const response = await authService.changePassword(currentUser.id, currentPassword, newPassword, changeDateTime);
      console.log('Change Password Response:', response);
      setSuccess(response.message || 'Password changed successfully! You will be redirected to login page in 5 seconds.');

      // Clear form fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Redirect to login after 5 seconds
      setTimeout(() => {
        logout(async () => {
          navigate('/login');
        });
      }, 5000);
      
    } catch (err) {
      setError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator - updated descriptions
  const renderPasswordStrength = () => {
    const strengthText = ['Weak', 'Fair', 'Good', 'Strong'];
    const strengthColor = ['#dc3545', '#ffc107', '#0d6efd', '#198754'];
    
    if (newPassword.length === 0) return null;
    
    return (
      <div className="mt-1">
        <div className="d-flex align-items-center">
          <div className="me-2" style={{ width: '100%', height: '5px', backgroundColor: '#e9ecef' }}>
            {[...Array(4)].map((_, index) => (
              <div 
                key={index}
                style={{
                  width: '25%',
                  height: '100%',
                  display: 'inline-block',
                  backgroundColor: index < passwordStrength ? strengthColor[passwordStrength - 1] : 'transparent'
                }}
              />
            ))}
          </div>
          <small style={{ color: strengthColor[passwordStrength - 1] || '#6c757d' }}>
            {passwordStrength > 0 ? strengthText[passwordStrength - 1] : 'Weak'}
          </small>
        </div>
        {passwordStrength < 3 && (
          <small className="text-muted mt-1 d-block">
            Use 8-16 characters with uppercase, lowercase, and numbers for a stronger password
          </small>
        )}
        {newPassword.length > 0 && !isValidPassword(newPassword) && (
          <small className="text-danger mt-1 d-block">
            Only letters and numbers are allowed (no special characters)
          </small>
        )}
      </div>
    );
  };
  
  return (
    <>
      <Navbar />
      
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="card shadow-sm border-0">
              <div className="card-header text-white text-center py-3" style={{backgroundColor:"#B22222"}}>
                <h4 className="mb-0">Change Password</h4>
              </div>
              
              <div className="card-body p-4">
                {/* Show 24-hour restriction warning if applicable */}
                {timeUntilNextChange && (
                  <div className="alert alert-warning d-flex align-items-center mb-4" role="alert">
                    <Clock size={18} className="me-2" />
                    <div>
                      <strong>Password Change Restriction:</strong> You can change your password again in {timeUntilNextChange.hours} hours and {timeUntilNextChange.minutes} minutes.
                    </div>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger d-flex align-items-center" role="alert">
                    <AlertCircle size={18} className="me-2" />
                    <div>{error}</div>
                  </div>
                )}
                
                {success && (
                  <div className="alert alert-success d-flex align-items-center" role="alert">
                    <CheckCircle size={18} className="me-2" />
                    <div>{success}</div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="currentPassword" className="form-label fw-bold">Current Password</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <Lock size={18} />
                      </span>
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        className="form-control py-2"
                        id="currentPassword"
                        placeholder="Enter your current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        disabled={timeUntilNextChange !== null}
                      />
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        aria-label="Toggle password visibility"
                        disabled={timeUntilNextChange !== null}
                      >
                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="newPassword" className="form-label fw-bold">New Password</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <Lock size={18} />
                      </span>
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        className="form-control py-2"
                        id="newPassword"
                        placeholder="8-16 characters, letters and numbers only"
                        value={newPassword}
                        onChange={handleNewPasswordChange}
                        disabled={timeUntilNextChange !== null}
                        maxLength={16}
                      />
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        aria-label="Toggle password visibility"
                        disabled={timeUntilNextChange !== null}
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {renderPasswordStrength()}
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="form-label fw-bold">Confirm New Password</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <Lock size={18} />
                      </span>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        className="form-control py-2"
                        id="confirmPassword"
                        placeholder="Confirm your new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={timeUntilNextChange !== null}
                        maxLength={16}
                      />
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label="Toggle password visibility"
                        disabled={timeUntilNextChange !== null}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {confirmPassword && newPassword && confirmPassword !== newPassword && (
                      <small className="text-danger mt-1 d-block">Passwords do not match</small>
                    )}
                    {confirmPassword && newPassword && confirmPassword === newPassword && (
                      <small className="text-success mt-1 d-block">Passwords match</small>
                    )}
                  </div>
                  
                  <div className="d-grid gap-2">
                    <button 
                      type="submit" 
                      style={{backgroundColor:"#B22222"}}
                      className="btn btn-primary py-2"
                      disabled={loading || timeUntilNextChange !== null}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Updating Password...
                        </>
                      ) : timeUntilNextChange ? (
                        `Available in ${timeUntilNextChange.hours}h ${timeUntilNextChange.minutes}m`
                      ) : (
                        'Update Password'
                      )}
                    </button>
                  </div>
                </form>
              </div>
              
              <div className="card-footer bg-white text-center py-3 border-0">
                <small className="text-muted">
                  {timeUntilNextChange ? 
                    'Password changes are limited to once every 24 hours for security purposes.' :
                    'Password must be 8-16 characters with letters and numbers only. After changing, you will be logged out.'
                  }
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default ChangePassword;